<template>
  <div class="product-accessories-view" v-if="accessories">
    <table
      class="uk-table uk-table-divider uk-margin-remove"
      v-if="accessories.length"
    >
      <thead>
        <tr>
          <th class="uk-table-shrink"></th>
          <th colspan="2">{{$t('fields.name')}}</th>
        </tr>
      </thead>
      <draggable
        :options="{ handle: '.drag-handle' }"
        @change="moveAccessory"
        element="tbody"
        v-model="accessories"
      >
        <tr
          :key="accessory.id"
          class="product-accessory-manager__row"
          v-for="accessory in accessories"
        >
          <td class="uk-table-shrink">
            <drag-handle :disabled="accessories.length <= 1" />
          </td>
          <td>
            <router-link
              :to="{ name: 'product',
                                params: { id: accessory.accessoryProduct.id } }"
            >{{accessory.accessoryProduct.name}}</router-link>
          </td>
          <td>
            <data-grid-actions
              :editable="false"
              @remove="deleteAccessoryWhenConfirmed(accessory)"
            />
          </td>
        </tr>
      </draggable>
      <tfoot>
        <tr>
          <td
            class="uk-padding-remove-left uk-padding-remove-right"
            colspan="3"
          >
            <common-button
              @click="$refs.accessoryFormDialog.show()"
              icon="plus"
              type="primary"
            >{{$t('actions.add')}}</common-button>
          </td>
        </tr>
      </tfoot>
    </table>

    <div class="uk-placeholder uk-text-center uk-margin-remove" v-else>
      <div class="uk-margin-bottom">{{$t('hints.empty')}}</div>
      <common-button
        @click="$refs.accessoryFormDialog.show()"
        icon="plus"
        type="primary"
      >{{$t('actions.add')}}</common-button>
    </div>

    <product-accessory-form
      :errors="errors"
      :unselectable-product-ids="unselectableProductIds"
      @submit="insertAccessory"
      ref="accessoryFormDialog"
    />
  </div>

  <div class="uk-margin-medium-top uk-text-center" v-else>
    <loader />
  </div>
</template>

<script>
import UIkit from 'uikit';
import View from '@/mixins/View';
import Authentication from '@/mixins/Authentication';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import deleteProductAccessoryMutation
  from '@/api/mutations/delete-product-accessory.graphql';
import Draggable from 'vuedraggable';
import DragHandle from '@/components/DragHandle';
import insertProductAccessoryMutation
  from '@/api/mutations/insert-product-accessory.graphql';
import Loader from '@/components/Loader';
import productAccessoriesQuery from '@/api/queries/product-accessories.graphql';
import ProductAccessoryForm from '@/components/ProductAccessoryForm';
import Submittable from '@/mixins/Submittable';
import Toast from '@/helpers/Toast';
import updateProductAccessoryMutation
  from '@/api/mutations/update-product-accessory.graphql';

export default {
  mixins: [
    Authentication,
    Submittable,
    View,
  ],
  components: {
    CommonButton,
    DataGridActions,
    Draggable,
    DragHandle,
    Loader,
    ProductAccessoryForm,
  },
  props: {
    product: Object,
  },
  computed: {
    breadcrumbs() {
      return [
        {
          key: 'show',
          text: () => this.$t('title'),
          route: { name: 'product-accessories' },
          isLoading: false,
        },
      ];
    },
    tableLoaded() {
      return this.tableInitiallyLoaded && this.accessories;
    },
    unselectableProductIds() {
      const ids = [this.product.id];
      if (!this.accessories) return ids;
      return ids.concat(this.accessories.map(r => r.accessoryProduct.id));
    },
  },
  methods: {
    async insertAccessory(formData) {
      const operation = await this.submitWithMutation({
        mutation: insertProductAccessoryMutation,
        variables: {
          accessory: {
            productId: this.product.id,
            accessoryProductId: formData.accessoryProductId,
          },
        },
        refetchQueries: [
          {
            query: productAccessoriesQuery,
            variables: { productId: this.product.id },
          },
        ],
      });
      if (operation.isValid) {
        const accessoryProduct = operation.accessory.accessoryProduct;
        const message =
          this.$t('toasts.created', { name: accessoryProduct.name });
        this.$refs.accessoryFormDialog.hide();
        Toast.success(message);
      }
    },
    async moveAccessory({ moved }) {
      const bundlingId = moved.element.id;
      const position = moved.newIndex + 1;
      await this.$apollo.mutate({
        mutation: updateProductAccessoryMutation,
        variables: { id: bundlingId, accessory: { position } },
        refetchQueries: [
          {
            query: productAccessoriesQuery,
            variables: { productId: this.product.id },
          },
        ],
      });
    },
    deleteAccessoryWhenConfirmed(accessory) {
      const message = this.$t('confirmations.delete', {
        name: accessory.accessoryProduct.name,
      });
      UIkit.modal
           .confirm(message)
           .then(() => { this.deleteAccessory(accessory); })
           .catch(() => {});
    },
    async deleteAccessory(accessory) {
      await this.$apollo.mutate({
        mutation: deleteProductAccessoryMutation,
        variables: { id: accessory.id },
        refetchQueries: [
          {
            query: productAccessoriesQuery,
            variables: { productId: this.product.id },
          },
        ],
      });
      const message = this.$t('toasts.deleted', {
        name: accessory.accessoryProduct.name,
      });
      Toast.success(message);
    },
  },
  apollo: {
    accessories: {
      query: productAccessoriesQuery,
      variables() {
        return { productId: this.product.id };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      update(data) {
        return data.productAccessories.entries;
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  i18n: {
    messages: {
      de: {
        title: 'Zubehör',
        confirmations: {
          delete: 'Zubehörprodukt wirklich entfernen?',
        },
        hints: {
          empty: 'Keine Zubehörprodukte gefunden.',
        },
        toasts: {
          created: 'Zubehörprodukt hinzugefügt.',
          deleted: 'Zubehörprodukt entfernt.',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// AccessoriesView.vue?6c2fac25